import React, {
  createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import {
  Box, Drawer, DrawerContent, DrawerOverlay, Flex, IconButton, Text,
} from '@chakra-ui/react';
import { useDragControls } from 'framer-motion';
import { LuX } from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import useSheetHistory from '../../hooks/useSheetHistory';
import useKeyboardInset from '../../hooks/useKeyboardInset';
import PhoneConfirmSheet from './PhoneConfirmSheet';

// A full sheet leaves a sliver of the page showing under the status bar, as
// iOS sheets do, so it reads as something that can be pulled away.
const FULL_HEIGHT = 'calc(100dvh - env(safe-area-inset-top) - 8px)';
const DETENT_HEIGHT = '70dvh';
// A drag down past this, or a fast flick, dismisses.
const DISMISS_PX = 120;
const DISMISS_VELOCITY = 800;
const EXPAND_PX = 60;

const PhoneSheetContext = createContext({ close: () => Promise.resolve() });

/**
 * For content inside a sheet: `close()` resolves once the sheet's history
 * entry is gone. Await it before navigating or opening anything else, or the
 * pending back step would undo the navigation.
 */
export function usePhoneSheet() {
  return useContext(PhoneSheetContext);
}

/**
 * The phone's bottom sheet.
 *
 * A Chakra Drawer that opens without focusing anything (so no keyboard rises
 * until the user taps a field) and does not hand focus back on close (so
 * closing a picker does not re-focus the field under it). The iOS back swipe
 * closes it through `useSheetHistory`. It can be dragged down from its
 * grabber or header, never from the body, which scrolls.
 *
 * `size`: 'full' (nearly the whole screen), 'content' (as tall as its content)
 * or 'detent' (opens at 70% and grows to full when dragged up, scrolled, or
 * typed into: the keyboard would leave too little of it at 70%).
 *
 * The header puts the title on the left with the actions on the right, or,
 * with a `headerLeft`, centres it between the two, with an optional quiet
 * `subtitle` line under it. A sheet with no other way
 * out gets a ✕: a full one always, a content one once it is as tall as the
 * screen allows and no dimmed page is left to tap.
 *
 * A sheet with unsaved edits (`isDirty`) asks before it closes, from any
 * control or the back swipe: through `onDirtyClose` when given, otherwise with
 * its own "Discard?" question.
 */
export default function PhoneSheet({
  id,
  isOpen,
  onClose,
  size = 'content',
  title,
  subtitle,
  headerLeft,
  headerRight,
  footer,
  isDirty = false,
  onDirtyClose,
  children,
}) {
  const { t } = useTranslation('common');
  const dragControls = useDragControls();
  // Only an open sheet listens: every page mounts several closed ones.
  const { inset, keyboardUp } = useKeyboardInset(isOpen);
  const [expanded, setExpanded] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // False until the sheet has slid in: a quick second tap on whatever opened
  // it lands on the empty space the sheet is sliding into, which would count
  // as a tap outside and close it again.
  const [settled, setSettled] = useState(false);
  const contentRef = useRef(null);
  const [atMaxHeight, setAtMaxHeight] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setExpanded(false);
      setSettled(false);
    }
  }, [isOpen]);

  // And again as it opens, before paint: the closing slide keeps the last
  // open render's handlers, so a scroll landing during it can set
  // `expanded` after the reset above, and the next open would start full.
  useLayoutEffect(() => {
    if (isOpen) setExpanded(false);
  }, [isOpen]);

  // Whether a content-sized sheet has reached its maximum height.
  useEffect(() => {
    if (!isOpen || size !== 'content' || typeof ResizeObserver === 'undefined') {
      setAtMaxHeight(false);
      return undefined;
    }
    let observer = null;
    const measure = () => {
      const el = contentRef.current;
      if (!el) return;
      const max = parseFloat(window.getComputedStyle(el).maxHeight);
      setAtMaxHeight(Number.isFinite(max) && el.offsetHeight >= max - 1);
    };
    // The content mounts a frame after `isOpen` flips.
    const frame = requestAnimationFrame(() => {
      if (!contentRef.current) return;
      observer = new ResizeObserver(measure);
      observer.observe(contentRef.current);
      measure();
    });
    window.addEventListener('resize', measure);
    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [isOpen, size]);

  const askDiscard = useCallback(() => {
    if (onDirtyClose) onDirtyClose();
    else setConfirmOpen(true);
  }, [onDirtyClose]);

  const { close, isTop } = useSheetHistory(id, isOpen, {
    onPop: onClose,
    isDirty: () => isDirty,
    onDirtyPop: askDiscard,
  });

  const requestClose = useCallback(() => {
    if (isDirty) askDiscard();
    else close();
  }, [isDirty, askDiscard, close]);

  // Escape closes the top sheet. Chakra listens for it on the dialog, which
  // never has focus here (autoFocus is off, so opening raises no keyboard),
  // so a keyboard's Escape on a narrow window or a tablet reached nothing.
  // Only the top sheet answers, so a picker closes before the sheet under it.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key !== 'Escape' || event.defaultPrevented || !isTop()) return;
      // Handled: once this sheet leaves the stack, the one under it is on
      // top, and its listener may run next for this same key press.
      event.preventDefault();
      requestClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, isTop, requestClose]);

  const handleDragEnd = (event, info) => {
    const down = info.offset.y;
    if (size === 'detent' && !expanded && down < -EXPAND_PX) {
      setExpanded(true);
      return;
    }
    if (size === 'detent' && expanded && down > DISMISS_PX && info.velocity.y < DISMISS_VELOCITY * 2) {
      setExpanded(false);
      return;
    }
    if (down > DISMISS_PX || info.velocity.y > DISMISS_VELOCITY) requestClose();
  };

  const startDrag = (event) => dragControls.start(event);

  const full = size === 'detent' && (expanded || keyboardUp);
  const height = size === 'full' || full
    ? FULL_HEIGHT
    : size === 'detent' ? DETENT_HEIGHT : undefined;

  const closeButton = (
    <IconButton
      icon={<LuX size={22} />}
      variant="ghost"
      borderRadius="full"
      color="redesign.text"
      // 44px in landscape too, where the theme's minimum (base only) stops.
      minW="44px"
      minH="44px"
      aria-label={t('actions.close')}
      onClick={requestClose}
    />
  );
  // A full sheet always has a visible way out: as a home-screen app there is
  // no edge back swipe. So does a content sheet as tall as the screen allows,
  // which leaves no dimmed page to tap. `headerRight={null}` opts out when the
  // content has one.
  const needsClose = size === 'full' || (size === 'content' && atMaxHeight);
  const right = headerRight !== undefined ? headerRight : (needsClose && !headerLeft ? closeButton : null);
  const hasHeader = Boolean(title || headerLeft || right);

  const context = useMemo(() => ({ close }), [close]);

  return (
    <>
      <Drawer
        isOpen={isOpen}
        placement="bottom"
        onClose={requestClose}
        closeOnOverlayClick={settled}
        autoFocus={false}
        returnFocusOnClose={false}
      >
        <DrawerOverlay />
        <DrawerContent
          ref={contentRef}
          bg="redesign.bg.surface"
          // The sheet already is the surface: a card inside it is a fill.
          sx={{ '--phone-card-bg': 'var(--chakra-colors-redesign-bg-card)' }}
          color="redesign.text"
          maxW="100vw"
          h={height}
          maxH={FULL_HEIGHT}
          borderTopRadius="22px"
          // Landscape: keep the content clear of the sensor housing.
          pl="env(safe-area-inset-left)"
          pr="env(safe-area-inset-right)"
          // The keyboard covers the bottom of the layout viewport on iOS;
          // lift the footer and the end of the body above it.
          pb={`${inset}px`}
          transition="height 0.2s ease-out"
          motionProps={{
            drag: 'y',
            dragControls,
            dragListener: false,
            dragSnapToOrigin: true,
            dragConstraints: { top: 0, bottom: 0 },
            dragElastic: { top: size === 'detent' && !expanded ? 0.3 : 0.02, bottom: 1 },
            onDragEnd: handleDragEnd,
            onAnimationComplete: (definition) => {
              if (definition === 'enter') setSettled(true);
            },
          }}
        >
          <PhoneSheetContext.Provider value={context}>
            <Box
              onPointerDown={startDrag}
              flexShrink={0}
              style={{ touchAction: 'none' }}
              cursor="grab"
            >
              <Box w="36px" h="5px" borderRadius="3px" bg="redesign.text.faint" mx="auto" mt="8px" mb={hasHeader ? '2px' : '6px'} />
              {hasHeader && (headerLeft ? (
                <Box
                  display="grid"
                  // The sides keep room for their buttons; a long title
                  // truncates between them instead of running under them.
                  gridTemplateColumns="minmax(max-content, 1fr) minmax(0, auto) minmax(max-content, 1fr)"
                  alignItems="center"
                  minH="48px"
                  px="8px"
                >
                  <Flex justify="flex-start" minW={0}>{headerLeft}</Flex>
                  {subtitle ? (
                    <Box textAlign="center" minW={0} px="4px">
                      <Text textStyle="phoneHeadline" color="redesign.heading" noOfLines={1}>{title}</Text>
                      <Text textStyle="phoneMeta" color="phone.muted" noOfLines={1}>{subtitle}</Text>
                    </Box>
                  ) : (
                    <Text textStyle="phoneHeadline" color="redesign.heading" textAlign="center" noOfLines={1} px="4px">
                      {title}
                    </Text>
                  )}
                  <Flex justify="flex-end" minW={0}>{right}</Flex>
                </Box>
              ) : (
                <Flex align="center" justify="space-between" gap="8px" minH="48px" pl="16px" pr="8px">
                  <Text textStyle="phoneHeadline" color="redesign.heading" noOfLines={1} minW={0}>
                    {title}
                  </Text>
                  <Flex justify="flex-end" flexShrink={0}>{right}</Flex>
                </Flex>
              ))}
            </Box>
            <Box
              flex="1"
              minH={0}
              overflowY="auto"
              overscrollBehavior="contain"
              pb={footer ? 0 : 'calc(env(safe-area-inset-bottom) + 16px)'}
              onScroll={size === 'detent' && !full
                ? (e) => { if (e.currentTarget.scrollTop > 0) setExpanded(true); }
                : undefined}
            >
              {children}
            </Box>
            {footer && (
              <Box
                flexShrink={0}
                px="16px"
                pt="8px"
                pb={inset ? '8px' : 'calc(env(safe-area-inset-bottom) + 8px)'}
                borderTop="1px solid"
                borderColor="redesign.border"
              >
                {footer}
              </Box>
            )}
          </PhoneSheetContext.Provider>
        </DrawerContent>
      </Drawer>

      {!onDirtyClose && (
        <PhoneConfirmSheet
          isOpen={confirmOpen}
          title={t('confirm.discardChangesTitle')}
          confirmLabel={t('confirm.discard')}
          cancelLabel={t('confirm.keepEditing')}
          destructive
          onConfirm={() => { setConfirmOpen(false); close(); }}
          onCancel={() => setConfirmOpen(false)}
        />
      )}
    </>
  );
}
